import { formatForDisplay, type Modifier } from "@tanstack/react-hotkeys";
import classNames from "classnames";

import styles from "./Kbd.module.css";
import type { Hotkey } from "./useHotkey";

/**
 * A shortcut `Kbd` can render: one `useHotkey` takes, or a lone modifier
 * (`"Mod"`) for a hint whose other half is not a key, such as a modifier
 * held while scrolling.
 */
export type KbdHotkey = Hotkey | Modifier;

export type KbdProps = {
  /** Extra class(es) for call-site layout. */
  className?: string;
} & (
  | {
      /**
       * The shortcut to render, in the same platform-neutral form passed to
       * `useHotkey` — so the binding and its label cannot drift apart.
       */
      hotkey: KbdHotkey;
      label?: never;
    }
  | {
      /** Text for something done alongside keys that is not a key itself ("scroll"). */
      label: string;
      hotkey?: never;
    }
);

/**
 * Renders a keyboard shortcut, resolved for the viewer's platform.
 *
 * `"Mod+K"` renders as `⌘K` on Apple platforms and `Ctrl+K` elsewhere;
 * `"Escape"` renders as `Esc` everywhere. Taking the same `Hotkey` value the
 * binding uses is the point — a hardcoded label can go stale when the binding
 * changes, a derived one cannot.
 *
 * Rendered `aria-hidden`: the glyphs read poorly aloud (`⌘` is announced
 * inconsistently across screen readers), so the accessible name belongs on
 * the control this decorates — a button's `aria-label` or its tooltip.
 *
 * @example
 * ```tsx
 * <RobotoButton aria-label="Quick search">
 *   <Kbd hotkey="Mod+K" />
 * </RobotoButton>
 * ```
 */
export function Kbd({ hotkey, label, className }: KbdProps) {
  return (
    <kbd className={classNames(styles.kbd, className)} aria-hidden="true">
      {hotkey === undefined ? label : formatForDisplay(hotkey)}
    </kbd>
  );
}
